import i18n from '../../i18n';
import Nprogress from 'nprogress';
import { orderBy } from 'lodash';
import { isSmallDevice } from '../../services/utilities/devices';
import {
    MAP_MARGINS,
    PANEL_WIDTHS,
    RAD_PER_DEGREE
} from '../../constants';

export const nprogress = Nprogress.configure({ trickleSpeed: 40 });

export const owsServiceTypeOptions = [
    { value: 'WMS', text: 'WMS' },
    { value: 'WFS', text: 'WFS' },
    { value: 'WMTS', text: 'WMTS' },
    { value: 'OGC', text: 'OGC' }
];

/**
 * This is function replaces the angular.fromJson
 * @param {any} json
 * @return {object json or json} parsed json or json
 */
export const fromJson = (json) => {
    return typeof json === 'string'
        ? JSON.parse(json)
        : json;
};

export function availableLogos(bitmaps) {
    let logos = [];
    if (arrayNotEmpty(bitmaps)) {
        const bitmapTypeLogo = bitmaps.filter(item => item.imageType === 1);
        logos = convertToValueTextPair(bitmapTypeLogo, 'id', 'name');
    }
    return logos;
}

export function availableFavicons(bitmaps) {
    let favicons = [];
    if (arrayNotEmpty(bitmaps)) {
        const bitmapTypeFavicon = bitmaps.filter(item => item.imageType === 2);
        favicons = convertToValueTextPair(bitmapTypeFavicon, 'id', 'name');
    }
    return favicons;
}

export function capitalizeValue(value) {
    if (typeof value !== 'string') {
        return '';
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export function convertToValueTextPair(inputArray, key, value) {
    // Do not perform converting for invalid input
    if (!inputArray) {
        return [];
    }
    // Type check the input array
    if (!Array.isArray(inputArray)) {
        throw new Error('Type check error: The input parameter must be of type Array');
    }

    return inputArray.map((item) => {
        if (key && value) {
            return { value: item[`${key}`], text: item[`${value}`] };
        } else {
            return { value: item, text: item };
        }
    });
}

export function convertHexToRgbAValue(hex) {
    let c;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split('');
        if (c.length === 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c = `0x${c.join('')}`;
        return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},1)`;
    }
    throw new Error('Bad Hex');
}

/**
 * Get the ows type from uri's
 * @param  {*}      record [ows overview record]
 * @return {string}        [ows type]
 */
export function getOwsType(record) {
    let owsType = '';
    if (record.uri_WMS && record.uri_WMS.length > 1) {
        owsType = 'WMS';
    }
    if (record.uri_WFS && record.uri_WFS.length > 1) {
        if (owsType.length === 0) {
            owsType = 'WFS';
        } else {
            owsType = `${owsType}, WFS`;
        }
    }
    if (record.uri_WMTS && record.uri_WMTS.length > 1) {
        if (owsType.length === 0) {
            owsType = 'WMTS';
        } else {
            owsType = `${owsType}, WMTS`;
        }
    }
    if (record.uri_OGC && record.uri_OGC.length > 1) {
        owsType = 'OGC';
    }
    return owsType;
}

/**
 * Turn a value into a name.
 * @param  {string} value value from which a name is made
 * @return {string} the name from the value
 */
export function valueToName(value) {
    if (value && value.length > 0) {
        // 1. Make everything lowercase
        let name = value.toLowerCase();
        // 2. Capitalize the first character
        if (name.length > 1) {
            name = name.charAt(0).toUpperCase() + name.slice(1);
        } else {
            name = name.toUpperCase();
        }
        // 3. Replace underscores with space
        name = name.replace(/_/g, ' ');
        return name;
    }
    return value;
}

function $_getWHParams(parameters) {
    let wParam = null;
    let hParam = null;
    for (const parameter of parameters) {
        const tokens = parameter.split('=');
        if (tokens && tokens.length > 1) {
            const token = tokens[0].trim();
            const value = tokens[1].trim();
            if (token.trim() === 'w') {
                wParam = `${token}=${value}`;
            }
            if (token.trim() === 'h') {
                hParam = `${token}=${value}`;
            }
        }
    }
    return {
        wParam,
        hParam
    };
}

/**
 * Strip center and zoom and '&w=0&h=0' from URL parameters
 * @param  {string} value URL parameters
 * @return {string} the stripped URL parameters
 */
export function stripCenterAndZoomFromUrlParameters(urlParameters) {
    if (urlParameters) {
        // voeg de w en h paramaters toe
        const parameters = urlParameters.split('&');
        if (parameters && parameters.length > 0) {
            const params = $_getWHParams(parameters);
            // also strip '&w=0&h=0'
            const strippedUrl = `${params.wParam}&${params.hParam}`;
            return (strippedUrl !== 'w=0&h=0' ? `&${strippedUrl}` : '');
        }
    }
    return urlParameters;
}

export function arrayNotEmpty(inputArray) {
    if (!Array.isArray(inputArray)) {
        return false;
    }
    return inputArray && inputArray.length > 0;
}

/**
 * Get the organisation maximum number of filter results.
 * For mobile a user is logged in, which is bound to an organisation.
 * For web, a theme is bound to an organisation.
 * @returns {int} maxFeatures
 */
export function getOrganisationMaxFeatures(isMobile, mobileUserData, webTheme) {
    let maxFeatures;

    if (isMobile) {
        // For mobile, there is a user logged in
        maxFeatures = mobileUserData.organizationMaxFeatures;
    } else if (webTheme && webTheme.organization) {
        // For web, the organisation is related to the webtheme
        maxFeatures = webTheme.organization.maxFeatures;
    }

    return maxFeatures;
}

export function stringNotEmpty(inputString) {
    if (typeof inputString !== 'string') {
        return false;
    }

    return inputString.length > 0;
}

/**
 * Returns the string after colon
 * @param dataType
 */
export function removeXmlPrefix(dataType) {
    let calculatedDataType = dataType;

    if (typeof dataType === 'string' && dataType.length > 0) {
        const indexOfColon = dataType.indexOf(':');
        if (indexOfColon >= 0) {
            calculatedDataType = dataType.slice(indexOfColon + 1);
        }
    }
    return calculatedDataType;
}

/**
 * Double to string format function
 * @param  {number} value [description]
 * @return {string}       [description]
 */
export function doubleToString(value) {
    if (value) {
        try {
            const significant = 2;
            const p1 = parseInt(value, 10);
            const p2 = (value - p1).toString().slice(2, 2 + significant);
            if (p2 !== '') {
                return `${p1.toString()},${p2}`;
            } else {
                return `${p1.toString()}`;
            }
        } catch (e) {
            console.log(`doubleToString: ${e.message}`);
            // fallback: default JS format
            return value.ToString();
        }
    }
    return value;
}

/**
 * Sets the favicon of the html page
 * @param {any} icon
 */
export function setFavicon(icon) {
    const existingLink = document.querySelector('link[rel*=\'icon\']');
    const newLink = document.createElement('link');
    const link = existingLink || newLink;

    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = icon?.length > 0 ? icon : '/icons/favicon-32.png';

    if (!existingLink) {
        document.getElementsByTagName('head')[0].appendChild(link);
    }
}

/**
 * Returns input without * symbol
 * @param {any} input
 */
export function trimAsterisks(input) {
    return input.replace(/[*]/gi, '');
}

/**
 * Reverses date format from yyyy-mm-dd to dd-mm-yyyy
 * @param {any} input
 */
export function reverseDate(input) {
    if (input?.length > 0) {
        const datePart = input.match(/\d+/g);
        //          dd             mm              yyyy
        return `${datePart[2]}-${datePart[1]}-${datePart[0]}`;
    }
    return input;
}

/**
 * Removes duplicates and sorts a list
 * @param {any} list
 */
export function removeDuplicatesAndSort(list) {
    list = list.filter((elem, index, self) => self.findIndex(
        (t) => {
            return (t.text === elem.text);
        }) === index);

    return orderBy(list, [o => o.text.toLowerCase()]);
}

// make a name from a property for a toggle state
export const normalNameToPrintSettingsCheckboxName = (s) => {
    if (typeof s !== 'string') {
        return '';
    }
    return `show${s.charAt(0).toUpperCase()}${s.slice(1)}`;
};

/**
 * Replace smart quotes ’ with straight quotes '
 * @param {string} string - String with possible smart quotes that has to be cleaned
 * @returns {string} - String where smart quotes are replaced by straight quotes
 */
export function cleanString(string) {
    // Opening/Closing single quotes
    return string.replace(/[\u2018\u2019]/g, '\'');
}

/**
 * Removes duplicate themes from a list of (feedback) themes
 * @param {array} nonUniqueThemes list of themes that can contain duplicates
 * @returns {array} list of themes without duplicates
 */
export function getUniqueThemes(nonUniqueThemes) {
    const uniqueThemes = [];
    const uniqueThemeIds = [];

    nonUniqueThemes.forEach((theme) => {
        if (uniqueThemeIds.indexOf(theme.id) === -1) {
            uniqueThemeIds.push(theme.id);
            uniqueThemes.push(theme);
        }
    });

    return uniqueThemes;
}

export function getCoordinateArray(pointArray) {
    return pointArray
        .reduce((coordList, nextCoord, index) => {
            const isEvenIndex = index % 2 == 0;
            if (!isEvenIndex) {
                coordList[coordList.length - 1].push(Number(nextCoord));
            } else {
                coordList.push([Number(nextCoord)]);
            }

            return coordList;
        }, []);
}

export function squareToPoints(pointArray) {
    if (pointArray.length === 4) {
        return [pointArray[0], pointArray[1], pointArray[2], pointArray[1], pointArray[2], pointArray[3], pointArray[0], pointArray[3]];
    }
    return null;
}

// returns a function that performs a case-insensitive sort on input parameter 'prop' in array of objects
export const arraySortAscendingFunction = (prop, cleanText = true) => {
    return function (a, b) {

        // ignore leading quote (for example: 's-Gravenhage)
        let textA = '';
        let textB = '';

        if (a[prop]) {
            textA = (cleanText && a[prop].substring(0, 1) === '\'' ? a[prop].substring(1) : a[prop]);
        }

        if (b[prop]) {
            textB = cleanText && b[prop].substring(0, 1) === '\'' ? b[prop].substring(1) : b[prop];
        }

        textA = textA.toUpperCase(); // ignore upper and lowercase
        textB = textB.toUpperCase(); // ignore upper and lowercase

        if (textA < textB) {
            return -1;
        }
        if (textA > textB) {
            return 1;
        }
        return 0;
    };
};

export function legendOrderSortFunction(a, b) {
    if ((b.sortOrder != 0 && (a.sortOrder > b.sortOrder || (a.sortOrder == 0 && b.sortOrder != 0))) ||
        (a.sortOrder == b.sortOrder && (a.title.toLowerCase() > b.title.toLowerCase()))) {
        return 1;
    }
    if ((a.sortOrder != 0 && (a.sortOrder < b.sortOrder || (b.sortOrder == 0))) ||
        (a.sortOrder == b.sortOrder && (a.title.toLowerCase() < b.title.toLowerCase()))) {
        return -1;

    }
    return 0;
}

export const numberSortFunction = (prop) => (a, b) => {
    return a[prop] - b[prop];
};

// subSort
// Sort a range within an array
// i is the index where the subsection begins
// n is the number of elements to sort
// sortFx is the sorting function
export function subSort(arr, i, n, sortFx) {
    return [...arr.slice(0, i), ...arr.slice(i, i + n).sort(sortFx), ...arr.slice(i + n, arr.length)];
}

export const findUserSavedFilterEqualToDashboardFilterFunction = (filtersUserMap) => (dashboardFilterItem) => {
    // since filterId's can change in dashboard we need an alternative to compare a saved filter by the user
    // to a filter defined on a layer in dashboard
    // we use the combination of layerId, informationAttributeId (can be multiple within one layer) and name of filter
    // the name could be sufficient, but it won't hurt to also check on informationAttributeId to prevent errors on the user's side
    return filtersUserMap.find(
        userFilterItem =>
            userFilterItem.filter.informationAttributeId === dashboardFilterItem.informationAttributeId &&
            userFilterItem.filter.name === dashboardFilterItem.name
    );
};

export const getAddressResolutionIndex = (addressType) => {
    if (addressType === 'adres' || addressType === 'perceel') {
        return 14;
    } else if (addressType === 'postcode' || addressType === 'weg') {
        return 13;
    } else if (addressType === 'woonplaats') {
        return 8;
    } else if (addressType === 'gemeente') {
        return 7;
    } else {
        return 11;
    }
};

export const removeExtensionFromFilename = (filename) => {
    return filename.replace(/\.[^/.]+$/, '');
};

export const getCreateFeedbackData = ({ origFeedbackFields, feedbackTheme, organizationId, featureId, location, geometryType, gpsLocation, userId, userName }) => {
    const getFields = ({
        name,
        alias,
        type,
        defaultValue,
        visible,
        required,
        personalData,
        searchType,
        fileType,
        domainExtendable
    }) => {
        const { invalidFieldError, value, domain, state } =
            origFeedbackFields.find(
                (field) => field.name === name
            );
        return {
            state,
            invalidFieldError,
            name,
            alias,
            type,
            value,
            defaultValue,
            visible,
            domain,
            required,
            personalData,
            searchType,
            fileType,
            domainExtendable
        };
    };

    const getType = ({
        alias,
        id,
        keywords,
        geometryType: _geometryType,
        name,
        useInWebTheme,
        sendStatusUpdates,
        usePersonalData,
        whereClauses,
        contentTypeId,
        feedbackFields
    }) => ({
        alias,
        id,
        keywords,
        geometryType: _geometryType,
        name,
        useInWebTheme,
        sendStatusUpdates,
        usePersonalData,
        whereClauses,
        contentTypeId,
        feedbackFields: feedbackFields.map(getFields)
    });

    const _type = getType(feedbackTheme);
    return {
        organizationId,
        id: featureId,
        location,
        geometryType,
        creator: {
            id: userId,
            username: userName
        },
        gpsLocation,
        type: _type
    };
};

export const getCreateWfsTData = ({ origInformationAttributes, layerTheme, organizationId, layerId, featureObject, geometryType, gpsLocation, userId, userName }) => {
    const getType = ({
        active,
        checked,
        data,
        error,
        id,
        type,
        geometryType: _geometryType,
    }) => ({
        active,
        checked,
        error,
        id,
        type,
        data,
        informationAttributes: origInformationAttributes
    });
    const _type = getType(layerTheme);
    const _title = layerTheme.title;
    return {
        organizationId,
        id: layerId,
        title: _title,
        objectId: featureObject.feature.id_,
        location: featureObject.location,
        geometryType,
        creator: {
            id: userId,
            username: userName
        },
        gpsLocation,
        type: _type
    };
};

export const objectKeysToLowerCaseKeys = (obj) => {
    return Object.keys(obj).reduce((accumulator, key) => {
        accumulator[key.toLowerCase()] = obj[key];
        return accumulator;
    }, {});
};

export const truncateString = (str, limit) => {
    if (str?.length > limit) {
        return `${str.slice(0, limit)}...`;
    } else {
        return str;
    }
};

export const getMinMaxValueForInputNumber = (maxLength, type) => {
    const maxValue = maxLength ? parseInt('9'.repeat(maxLength)) : 999999999;
    return type === 'min' ? -maxValue : maxValue;
};

export const formatAddress = (data) => {
    if (data && data['plaats']) {
        const straatnaam = data['straatnaam'] ?? '';
        const postbusnummer = data['postbusnummer'] ?? '';
        const huisnummer = data['huisnummer'] ?? '';
        const huisletter = data['huisletter'] ?? '';
        const huisnummertoev = data['huisnummerToevoeging'] ?? '';
        const toevoegingAdres = data['toevoegingAdres'] ?? '';
        const postcode = data['postcode'] ?? '';
        const plaats = data['plaats'] ?? '';
        const land = data['land'] ?? '';
        return `${straatnaam} ${postbusnummer}${huisnummer}${huisletter}${huisnummertoev}${toevoegingAdres}, ${postcode} ${plaats} ${land}`;
    }
    return '';
};

export function getViewPadding(infoTableHeight, usePanelMargin = true) {
    const usePanelMarginLargeDevice = usePanelMargin && !isSmallDevice();
    const screenWidth = window.innerWidth;
    const margin = MAP_MARGINS.featuresZoom;

    const popupWidth = usePanelMarginLargeDevice
        ? PANEL_WIDTHS.INFORMATION
        : screenWidth / 2;

    const marginLeft = usePanelMarginLargeDevice
        ? popupWidth + margin
        : margin;

    const marginBottom = usePanelMarginLargeDevice
        ? infoTableHeight + margin
        : margin;

    return [margin, margin, marginBottom, marginLeft];
}

// Add 0 in front of BSN when length < 9
export const formatBsnWithLeadingZero = (bsn) => {
    return bsn.padStart(9, '0');
};

// Remove first 0 when BSN length = 9
export const formatBsnRemoveLeadingZero = (bsn) => {
    if (bsn && bsn.length === 9 && Array.from(bsn)[0] === '0') {
        return bsn.substring(1);
    } else {
        return bsn;
    }
};

export function RGBAToHexAndA(rgba) {
    let sep = rgba.indexOf(',') > -1 ? ',' : ' ';
    rgba = rgba.substr(5).split(')')[0].split(sep);

    // Strip the slash if using space-separated syntax
    if (rgba.indexOf('/') > -1) {
        rgba.splice(3, 1);
    }

    for (let index in rgba) {
        let red = rgba[index];
        if (red.indexOf('%') > -1) {
            let p = red.substr(0, red.length - 1) / 100;

            if (index < 3) {
                rgba[index] = Math.round(p * 255);
            } else {
                rgba[index] = p;
            }
        }
    }

    let r = (+rgba[0]).toString(16),
        g = (+rgba[1]).toString(16),
        b = (+rgba[2]).toString(16),
        a = rgba[3];

    if (r.length == 1) {
        r = `0${r}`;
    }
    if (g.length == 1) {
        g = `0${g}`;
    }
    if (b.length == 1) {
        b = `0${b}`;
    }

    return { hex: `#${r}${g}${b}`, a: Number(a) };
}

/**
 * Returns true if base64 image is a svg image
 * @param {string} base64String
 */
export function isSvgBase64Image(base64String) {
    return base64String.split(';')[0].split('/')[1].toLowerCase() === 'svg+xml';
}

// find all [variable] names in a string.
// A variable is enclosed by square brackets.
// return an array of variable names
export function getVariablesFromString(inputString) {
    var variables = [];

    if (inputString) {
        inputString.replace(/\[(.*?)\]/g, function (g0, g1) {
            variables.push(g1);
        });
    }

    return variables;
}

// input: a string containing an expression
//        an object containing property values to replace the variables in the string
export function evaluateNumericExpression(fn, valueObject) {
    // replace every occurence of [variable] with the value of the variable
    for (const [key, value] of Object.entries(valueObject)) {
        fn = fn.replaceAll(`[${key}]`, value);
    }

    //  check if result is correct
    //  numeric : may contain only numbers, a dot, brackets and operators + - / *
    const expressieRegex = /^[0-9 .()+\-/*]*$/;

    // comma somehow matches the regex so we filter it seperately
    if (fn.includes(',') || !expressieRegex.test(fn)) {
        throw new Error(
            i18n.t('errorExpressionNumericCharacters')
        );
    }

    return new Function(`return ${fn}`)();
}

// Get items that only occur in the left array, using the compareFunction to determine equality
export const onlyInLeftArray = (leftArray, rightArray, compareFunction) =>
    leftArray.filter(
        (leftValue) =>
            !rightArray.some((rightValue) =>
                compareFunction(leftValue, rightValue)
            )
    );
export function dataImportStatus(status) {
    return String(i18n.t(`dataImport.statusCodes.${status}`));
}

export function dataExportStatus(status) {
    return String(i18n.t(`dataExport.statusCodes.${status}`));
}

export function functionResultName(functionName) {
    return String(i18n.t(`functionResult.functionName.${functionName}`));
}

// return depth of an nested array
export function getArrayDepth(value) {
    return Array.isArray(value) ?
        1 + Math.max(0, ...value.map(getArrayDepth)) :
        0;
}

export function getImageUrlFromValue(value) {
    // storage methods for images in feedback database (1 and 2 are old and not used after the V0 => V1 merge):
    // 1: "https://api-web-nedglobe.nedgraphicscs.nl/api/file/getimage/n6HH4IVMDuGGX6cwOCvESN6ix4BzEttg|jpeg"
    // 2: "https://api-web-nedglobe.nedgraphicscs.nl/api/file/getimage/n6HH4IVMDuGGX6cwOCvESN6ix4BzEttg"
    // 3: "n6HH4IVMDuGGX6cwOCvESN6ix4BzEttg"
    // return: imagedId: "n6HH4IVMDuGGX6cwOCvESN6ix4BzEttg"
    //
    const imageUrlSplit = value.split('/');
    const imageId = imageUrlSplit[imageUrlSplit.length - 1].split('|')[0];
    // Build the URL and SET
    return `/api/v1/images/feedbackimage/${imageId}`;
}

export function isValidUri(uri) {
    // URI regex
    const pattern = new RegExp('^https?:\\/\\/' +                      // mandatory protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +    // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' +                         // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +                     // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' +                            // query string (not in use with OWS)
        '(\\#[-a-z\\d_]*)?$', 'i');
    return pattern.test(uri);
}

// Checks if uri contains forbidden words
export function uriHasInvalidWords(uri) {
    if (!stringNotEmpty(uri)) {
        return false;
    }

    const forbiddenWords = [
        '?SERVICE=',
        '&SERVICE=',
        '?REQUEST=',
        '&REQUEST='
    ];

    // return true if there's at least one
    return forbiddenWords.some((s) => uri.toUpperCase().includes(s));
}

export function DegreesToRadians(deg) {
    return deg * RAD_PER_DEGREE;
}

export function RadiansToDegrees(rad) {
    return rad / RAD_PER_DEGREE;
}

export function getWebThemeUrl(webTheme) {
    return `${window.location.origin}/web?tma=${webTheme.guid}&${webTheme.urlParameters}`;
}

export function getWebThemeShortUrl(webTheme) {
    // setup the link to the map, this is a short url without center and zoom and without "&w=0&h=0"
    const strippedUrlParameters = stripCenterAndZoomFromUrlParameters(
        webTheme.urlParameters
    );
    const urlId = webTheme.useGuid ? webTheme.guid : webTheme.id;
    return `${window.location.origin}/web?tma=${urlId}${strippedUrlParameters}`;
}

export function getWebThemePortalUrl(webTheme) {
    const urlId = webTheme.useGuid ? webTheme.guid : webTheme.id;
    return `${window.location.origin}/web?tma=${urlId}&${webTheme.urlParameters}&p=y`;
}

export function checkTmaValue(tma) {
    // Regular expression for matching a GUID
    const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

    // Check if the value is an integer
    if (!isNaN(tma) && Number.isInteger(parseFloat(tma))) {
        return 'integer';
    } else if (guidRegex.test(tma)) {
        // Check if the value matches the GUID pattern
        return 'guid';
    } else {
        return 'invalid';
    }
}
// Allow webtheme maps to communicate between localhost and DEVNG
export function windowMessageReceiver(origin) {
    if (origin === 'https://localhost:44309' || origin === 'https://nedglobe-devng.cadac.com') {
        return '*';
    } else {
        return origin;
    }
}